import { Box, Typography, Stack, Avatar } from "@mui/material";
import { Message } from "../models/Message";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

interface ChatGroupBubbleProps {
  messages: Message[];
  showSender: boolean;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
}

const ChatGroupBubble = ({
  messages,
  showSender,
  isFirstInGroup,
  isLastInGroup,
}: ChatGroupBubbleProps) => {
  const firstMessage = messages[0];
  const isIncoming = firstMessage.direction === "incoming";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: isIncoming ? "flex-start" : "flex-end",
        mb: isLastInGroup ? 2 : 0.5,
      }}
    >
      {/* Sender info (only shown for first in group) */}
      {showSender && isFirstInGroup && firstMessage.sender && (
        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5, ml: 6 }}>
          <Avatar
            sx={{
              width: 24,
              height: 24,
              mr: 1,
              bgcolor: isIncoming ? "primary.main" : "secondary.main",
            }}
          >
            {firstMessage.sender.charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="caption" color="text.secondary">
            {firstMessage.sender}
          </Typography>
        </Box>
      )}

      {/* Messages stack */}
      <Stack spacing={0.5}>
        {messages.map((message, index) => (
          <Box
            key={message.id}
            sx={{
              bgcolor: isIncoming ? "#f0f0f0" : "#007aff",
              color: isIncoming ? "#000" : "#fff",
              borderRadius:
                isFirstInGroup && index === 0
                  ? isIncoming
                    ? "18px 18px 18px 4px"
                    : "18px 18px 4px 18px"
                  : isLastInGroup && index === messages.length - 1
                  ? isIncoming
                    ? "4px 18px 18px 18px"
                    : "18px 4px 18px 18px"
                  : "4px 18px",
              p: 1.5,
              maxWidth: "80%",
              position: "relative",
            }}
          >
            <Typography variant="body1">{message.content}</Typography>

            {/* Timestamp and status (only shown for last message in bubble) */}
            {index === messages.length - 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  mt: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.7,
                    mr: 0.5,
                    color: isIncoming
                      ? "text.secondary"
                      : "rgba(255,255,255,0.7)",
                  }}
                >
                  {new Date(message.timestamp).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
                {message.direction === "outgoing" &&
                  (message.status === "read" ? (
                    <CheckCircleIcon sx={{ fontSize: 14, color: "#4fc3f7" }} />
                  ) : message.status === "delivered" ? (
                    <CheckCircleOutlineIcon
                      sx={{
                        fontSize: 14,
                        color: isIncoming ? undefined : "#fff",
                      }}
                    />
                  ) : (
                    <CheckCircleOutlineIcon
                      sx={{
                        fontSize: 14,
                        opacity: 0.5,
                        color: isIncoming ? undefined : "#fff",
                      }}
                    />
                  ))}
              </Box>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default ChatGroupBubble;
